import { useState, useRef, useEffect, useMemo } from "react";

import { default as styled } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import {
    useRecentDomains,
    useDomainSuggestions,
} from "app/pages/digital-marketing/monitor-and-protect/services/domain-search";
import { Chip } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/chip/chip";
import { Favicon } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/layout/favicon";
import { isValidDomainOrURL } from "app/pages/digital-marketing/utils/validation/url/domainValidation";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

const DomainContainer = styled(Flex)`
    flex-wrap: wrap;
    max-height: 170px;
    overflow: auto;
    border: 1px solid ${colorsPalettes.midnight["50"]};
    border-bottom: none;

    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    & + .domain-input {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
`;

const DomainTrigger = styled(Flex)<{ $hasDomainsAbove: boolean }>`
    border: 1px solid ${colorsPalettes.midnight["50"]};
    border-radius: ${({ $hasDomainsAbove }) => ($hasDomainsAbove ? "0 0 3px 3px" : "3px")};
    background-color: ${colorsPalettes.carbon["0"]};
    cursor: text;
    min-height: 40px;
`;

interface IDomainManagerProps {
    title: string;
    domains?: string[];
    placeholder?: string;
    strikethrough?: boolean;
    searchingText?: string;
    onDomainsChange: (domains: string[]) => void;
}

const ContentStyled = styled(Combobox.Content)`
    max-height: 214px;
`;

export const DomainManager = ({
    title,
    domains = [],
    onDomainsChange,
    placeholder,
    strikethrough,
    searchingText = "Searching domains...",
}: IDomainManagerProps) => {
    const [searchValue, setSearchValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [triggerHeight, setTriggerHeight] = useState(48);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Use separate hooks for suggestions and recents
    const { data: recent = [], isLoading: isRecentLoading } = useRecentDomains();
    const { suggestions, isLoading: isSuggestionsLoading } = useDomainSuggestions({
        term: searchValue,
        excludeDomains: domains,
    });

    const isLoading = searchValue.trim() ? isSuggestionsLoading : isRecentLoading;

    useEffect(() => {
        const resizeHandler = () => {
            if (triggerRef.current) {
                setTriggerHeight(triggerRef.current.offsetHeight);
            }
        };
        const resizeObserver = new ResizeObserver(resizeHandler);
        if (triggerRef.current) {
            resizeObserver.observe(triggerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const parseDomains = (input: string): string[] => {
        return input
            .split(",")
            .map((domain) => domain.trim().toLowerCase())
            .filter((domain) => domain.length > 0 && isValidDomainOrURL(domain));
    };

    const getUniqueDomains = (input: string): string[] => {
        const parsedDomains = parseDomains(input);
        const domainSet = new Set(domains.map((d) => d.toLowerCase()));
        const newDomainsSet = new Set(parsedDomains);

        return [...newDomainsSet.difference(domainSet)];
    };

    const handleRemoveDomain = (domainToRemove: string) => {
        onDomainsChange(domains.filter((domain) => domain.toLowerCase() !== domainToRemove.toLowerCase()));
    };

    const handleDomainSelect = (domain: string) => {
        if (!domains.some((d) => d.toLowerCase() === domain.toLowerCase())) {
            onDomainsChange([...domains, domain]);
        }
        setSearchValue("");
        setIsOpen(false);
    };

    const handleSubmit = (value: string) => {
        const uniqueDomains = getUniqueDomains(value);
        if (uniqueDomains.length > 0) {
            onDomainsChange([...domains, ...uniqueDomains]);
            setSearchValue("");
            setIsOpen(false);
        }
    };

    // Get domain suggestions from the API or recent domains
    const allSuggestions = useMemo(() => {
        return [
            ...suggestions.map((suggestion) => ({
                name: suggestion.name,
                image: suggestion.image,
            })),
            ...(searchValue.trim() ? [] : recent),
        ];
    }, [suggestions, recent, searchValue]);

    // Filter out already selected domains and ensure uniqueness
    const filteredSuggestions = useMemo(() => {
        return allSuggestions
            .filter((suggestion) => !domains.some((d) => d.toLowerCase() === suggestion.name.toLowerCase()))
            .reduce(
                (unique, suggestion) => {
                    const exists = unique.some(
                        (item) => item.name.toLowerCase().trim() === suggestion.name.toLowerCase().trim(),
                    );
                    if (!exists) {
                        unique.push(suggestion);
                    }

                    return unique;
                },
                [] as Array<{ name: string; image?: string }>,
            )
            .slice(0, 10); // Limit to 10 suggestions
    }, [allSuggestions, domains]);

    const showCustomDomainOption = useMemo(() => {
        const searchTermCleanValue = searchValue.trim();
        const searchTermNotEmpty = !!searchTermCleanValue;
        const searchTermIsValidDomain = isValidDomainOrURL(searchTermCleanValue);
        const searchTermIsNotInDomains = !filteredSuggestions.some(
            (suggestion) => suggestion.name.toLowerCase().trim() === searchTermCleanValue.toLowerCase(),
        );

        return searchTermNotEmpty && searchTermIsValidDomain && searchTermIsNotInDomains;
    }, [filteredSuggestions, searchValue]);

    return (
        <Stack
            width="100%"
            minWidth="350px"
            gap="8px"
        >
            <Flex>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                >
                    {title}
                </Text>
            </Flex>
            <Stack>
                {domains.length > 0 ? (
                    <DomainContainer
                        padding="8px 12px"
                        borderRadius="3px"
                        gap="8px"
                    >
                        {domains.map((domain, index) => (
                            <Chip
                                key={index}
                                strikethrough={strikethrough}
                                onCloseItem={() => handleRemoveDomain(domain)}
                                text={domain}
                            />
                        ))}
                    </DomainContainer>
                ) : null}
                <Combobox
                    className="domain-input"
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    width={
                        triggerRef?.current?.getBoundingClientRect().width
                            ? `${triggerRef.current.getBoundingClientRect().width}px`
                            : "400px"
                    }
                    placement="bottom-start"
                    offset={-(triggerHeight + 8)}
                >
                    <Combobox.Trigger ref={triggerRef}>
                        <DomainTrigger
                            alignItems="center"
                            padding="8px 10px"
                            $hasDomainsAbove={domains.length > 0}
                            onClick={() => setIsOpen(true)}
                            gap="12px"
                        >
                            <SWReactIcons
                                iconName="globe"
                                size="sm"
                                iconColor={colorsPalettes.carbon["200"]}
                            />
                            <Text
                                fontVariant="input2"
                                color={searchValue ? colorsPalettes.carbon["500"] : colorsPalettes.carbon["100"]}
                            >
                                {searchValue || placeholder}
                            </Text>
                        </DomainTrigger>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={placeholder}
                        icon="globe"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSubmit(searchValue);
                            }
                        }}
                    />

                    <ContentStyled>
                        {isLoading ? (
                            <Combobox.Option
                                key="loading"
                                isDisabled
                            >
                                <Flex
                                    alignItems="center"
                                    gap="8px"
                                >
                                    <Text
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon["300"]}
                                    >
                                        {searchingText}
                                    </Text>
                                </Flex>
                            </Combobox.Option>
                        ) : (
                            <>
                                {showCustomDomainOption ? (
                                    <Combobox.Option
                                        key="custom-domain"
                                        onClick={() => handleDomainSelect(searchValue.trim())}
                                    >
                                        <Flex
                                            alignItems="center"
                                            gap="8px"
                                        >
                                            <Favicon
                                                iconUrl={getFaviconUrl(searchValue.trim())}
                                                alt={searchValue.trim()}
                                                size={24}
                                            />
                                            <Combobox.OptionLabel>{searchValue.trim()}</Combobox.OptionLabel>
                                        </Flex>
                                    </Combobox.Option>
                                ) : null}
                                {filteredSuggestions.map((suggestion) => (
                                    <Combobox.Option
                                        key={suggestion.name}
                                        onClick={() => handleDomainSelect(suggestion.name)}
                                    >
                                        <Flex
                                            alignItems="center"
                                            gap="8px"
                                        >
                                            <Favicon
                                                iconUrl={suggestion.image || getFaviconUrl(suggestion.name)}
                                                alt={suggestion.name}
                                                size={24}
                                            />
                                            <Combobox.OptionLabel>{suggestion.name}</Combobox.OptionLabel>
                                        </Flex>
                                    </Combobox.Option>
                                ))}
                            </>
                        )}
                    </ContentStyled>
                </Combobox>
            </Stack>
        </Stack>
    );
};
